import { HeadersFunction, MetaFunction } from "react-router";
import { metaService } from "~/utils/metaService";
import cookiePolicyData from "~/data/policy/cookiePolicy.json";
import dayjs from "dayjs";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { cn } from "~/lib/utils";
import { useFontStyle } from "~/hook/useFontStyle";
import { computeEtagFromData } from "~/utils/helper-funcations";

export const meta: MetaFunction = ({ location, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const meta = getPageMeta("cookie", lang);

  return metaService({
    title: meta.metaTitle,
    description: meta.metaDescription,
    ogTitle: meta.ogTitle,
    ogDescription: meta.ogDescription,
    ogUrl: pathname,
    ogImg: meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: cookiePolicyData.hreflangs.map((lang) => ({
      ...lang,
      href: `${lang.href}${pathname}`,
    })),
  });
};

export const loader = ({ params, request }: { params: { lang: string }; request: Request }) => {
  const lang = params.lang || LANG.english;

  const DATA = {
    title: lang === LANG.english ? "Cookie Policy" : "કૂકી નીતિ",
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");
  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        Etag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.POLICY.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

function CookiePolicy() {
  const data = cookiePolicyData.content;
  const { normalInter, medium } = useFontStyle();

  const SectionComponenet = ({
    title,
    paragraphs,
    isCurrentDate = false,
    className,
  }: {
    title: string;
    paragraphs: string[];
    isCurrentDate?: boolean;
    className?: string;
  }) => {
    return (
      <section className={className}>
        <h1 className={cn("h4_heading text-textColor mb-6", medium)}>{title}</h1>
        <div className="space-y-4">
          {paragraphs.map((paragraph, index) => (
            <p
              key={index}
              className={cn("leading-relaxed text-textSecondaryColor body_text_m", normalInter)}
              dangerouslySetInnerHTML={{ __html: paragraph }}
            />
          ))}
        </div>
        {isCurrentDate ? (
          <p className={cn("body_text_s text-textSecondaryColor mt-4", normalInter)}>Last updated: {dayjs().format("MMMM YYYY")}</p>
        ) : null}
      </section>
    );
  };

  return (
    <div className={cn("text-textColor space-y-8 max-w-4xl mx-auto p-4")}>
      {/* Introduction */}
      <SectionComponenet title={data.introduction.title} paragraphs={data.introduction.paragraphs} isCurrentDate />

      {/* What Are Cookies */}
      <SectionComponenet title={data.whatAreCookies.title} paragraphs={data.whatAreCookies.paragraphs} />

      {/* Types of Cookies */}
      <section>
        <h2 className={cn("h4_heading  text-textColor mb-4", medium)}>{data.typesOfCookies.title}</h2>
        <div className="space-y-6">
          {data.typesOfCookies.categories.map((category, index) => (
            <div key={index} className="border-l-4 border-borderPrimary pl-6 bg-secondaryColor p-4 rounded-r-lg">
              <h3 className={cn("body_text_l text-textColor mb-2", medium)}>{category.name}</h3>
              <p className={cn("mb-3 body_text_m", normalInter)}>{category.description}</p>
              <div>
                <p className={cn("body_text_m text-textColor mb-2", medium)}>Examples:</p>
                <ul className={cn("list-disc list-inside space-y-1 text-textSecondaryColor body_text_s", normalInter)}>
                  {category.examples.map((example, exampleIndex) => (
                    <li key={exampleIndex}>{example}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Third-Party Cookies */}
      <SectionComponenet title={data.thirdPartyCookies.title} paragraphs={data.thirdPartyCookies.paragraphs} />

      {/* Cookie Management */}
      <SectionComponenet title={data.cookieManagement.title} paragraphs={data.cookieManagement.paragraphs} />

      {/* Browser Settings */}

      <section>
        <h2 className={cn("h4_heading text-textColor mb-4", medium)}>{data.browserSettings.title}</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {data.browserSettings.browsers.map((browser, index) => (
            <div key={index} className="bg-cardBg dark:bg-cardBg p-4 rounded-lg">
              <h3 className={cn("body_text_m text-textColor mb-2", medium)}>{browser.name}</h3>
              <p className={cn("body_text_s text-textSecondaryColor", normalInter)}>{browser.instructions}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Data Protection */}
      <SectionComponenet title={data.dataProtection.title} paragraphs={data.dataProtection.paragraphs} />

      {/* Updates */}
      <SectionComponenet title={data.updates.title} paragraphs={data.updates.paragraphs} />

      {/* Contact */}
      <SectionComponenet title={data.contact.title} paragraphs={data.contact.paragraphs} className="bg-cardBg p-6 rounded-lg" />
    </div>
  );
}

export default CookiePolicy;
